import { GridSortItem } from "@mui/x-data-grid"
import { usePrevious } from "@uidotdev/usehooks"
import Joi from "joi"
import _, { get, isEqual } from "lodash"
import { useCallback, useState } from "react"
import { useAppDispatch, useAppSelector } from "src/app/hooks"
import { fetchSl2Api } from "src/features/baseQuerySl2"
import { selectTenantId } from "src/features/me/meSlice"
import {
  REPORT_DATA_EXPORT_PATH,
  ReportEnvelope,
  resolveExportFile,
  useRequestReportDataMutation,
} from "src/features/report/reportApi"
import {
  setReportIsLoading,
  upsertDatasets,
} from "src/features/report/reportSlice"

const useReportDatasets = (reportType: string) => {
  Joi.assert(reportType, Joi.string().required())
  console.log("useReportDatasets reportType", reportType)

  const tenantId = useAppSelector(selectTenantId)

  const [requestReportData, { isLoading }] = useRequestReportDataMutation()

  // const reservationFilters = useAppSelector(selectReservationFilters)

  // const clientFilters = useAppSelector(selectClientFilters)

  // const lockerIdFilters = useAppSelector(selectLockerIdFilters)

  const dispatch = useAppDispatch()

  const [pagination, setPagination] = useState({ page: 0, pageSize: 100 })

  const prevPagination = usePrevious(pagination)

  const shouldRefetch = !isEqual(prevPagination, pagination)

  let [hasNext, setHasNext] = useState(false)

  const [sortModel, setSortModel] = useState<GridSortItem>({
    field: "r.created_at",
    sort: "desc",
  })

  const [error, setError] = useState("")

  const refresh = useCallback(
    (filters: Record<string, any> = {}) => {
      const f = async (filters: Record<string, any> = {}) => {
        setError("")
        if (!tenantId) {
          return
        }
        try {
          const input = {
            tenantId,
            pagination: {
              page: pagination.page + 1, // backend is 1 based
              pageSize: pagination.pageSize,
            },
            sortModel,
          } as Record<string, any>
          if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
              if (!_.isUndefined(value) && key) {
                input[key] = value
              }
            })
          }

          dispatch(setReportIsLoading(true))

          const res = await requestReportData({
            body: {
              reportType,
              tenantId,
              input,
            },
          }).unwrap()
          const datasets = get(res, "res.datasets", {})
          dispatch(upsertDatasets(datasets))
          setHasNext(get(res, "res.hasNext", false))
        } catch (e: any) {
          console.error("Error fetching report data", e)
          setError("Error fetching report data")
        } finally {
          dispatch(setReportIsLoading(false))
        }
      }
      f(filters)
    },
    [
      dispatch,
      pagination.page,
      pagination.pageSize,
      reportType,
      requestReportData,
      // clientFilters,
      // lockerIdFilters,
      // reservationFilters,
      sortModel,
      tenantId,
    ],
  )

  // useEffect(() => {
  //   if (shouldRefetch) {
  //     refresh()
  //   }
  // }, [refresh, pagination, shouldRefetch])

  const [isFetchingExport, setIsFetchingExport] = useState(false)

  // Set when the last export hit the row cap, so the page can say so (N4).
  const [exportTruncation, setExportTruncation] = useState<
    ReportEnvelope["truncation"] | null
  >(null)

  const fetchExportDownload = useCallback(
    async (
      outputFormat: string,
      filters: Record<string, any> = {},
      anonymize: boolean = false,
    ) => {
      setIsFetchingExport(true)
      setError("")
      setExportTruncation(null)
      try {
        const input = {
          tenantId,
          pagination: {
            page: pagination.page + 1, // backend is 1 based
            pageSize: pagination.pageSize,
          },
          output: outputFormat,
          sortModel,
          anonymize,
        } as Record<string, any>

        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (!_.isUndefined(value) && key) {
              input[key] = value
            }
          })
          // if (reservationFilters) {
          //   input.reservationFilters = reservationFilters
          // }
          // if (clientFilters) {
          //   input.clientFilters = clientFilters
          // }
          // if (lockerIdFilters) {
          //   input.lockerIdFilters = lockerIdFilters
          // }
        }

        const response = await fetchSl2Api(REPORT_DATA_EXPORT_PATH, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reportType,
            tenantId,
            input,
          }),
        })

        if (!response.ok) {
          console.error("Error fetching report data file", response.statusText)
          setError("Error exporting report data")
          return
        }

        // An envelope pointing at the file in S3 (tenant flag
        // ReportDataViaS3 on), or the inline base64 file (off).
        const file = await resolveExportFile(response)
        if (!file) {
          setError("Error exporting report data")
          return
        }
        setExportTruncation(file.truncation ?? null)

        const { filename, blob } = file

        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.style.display = "none"
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        console.error("Error fetching report data file", e)
        setError("Error exporting report data")
      } finally {
        setIsFetchingExport(false)
      }
    },
    [
      tenantId,
      pagination.page,
      pagination.pageSize,
      sortModel,
      reportType,
      // reservationFilters,
      // clientFilters,
      // lockerIdFilters,
    ],
  )
  if (!tenantId) {
    return {
      refresh: () => {},
      isLoading: false,
      pagination,
      setPagination,
      hasNext: false,
      fetchExportDownload: () => {},
      isFetchingExport,
      exportTruncation: null,
      sortModel,
      setSortModel,
      error,
    }
  }

  return {
    refresh,
    isLoading,
    pagination,
    setPagination,
    hasNext,
    fetchExportDownload,
    isFetchingExport,
    exportTruncation,
    sortModel,
    setSortModel,
    error,
  }
}

export default useReportDatasets
